import CreateEditUserModal from '@components/organisms/user/CreateEditUserModal';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import EnableUserModal from '@components/organisms/user/EnableUserModal';
import { EditIcon24 } from '@components/ui/Icons';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { Modal } from '@components/molecules/Modal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { UserMembershipsCell } from '@components/molecules/UserMembershipsCell';
import { useUpdateUserOrganizationRole } from '@hooks/api/users';
import { Organization } from '@models/organization.model';
import { User, UserLocale, UserRoles, UserStatus } from '@models/user.model';
import { NoAccounts } from '@mui/icons-material';
import formatDate, { formatDateOnly } from '@utils/formatDate';
import { useState } from 'react';

interface UserListProps {
  handleDisableUser: (userId: string) => void;
  handleEnableUser: (userId: string) => void;
  handleEditUser: (
    organizationId: string,
    status: UserStatus,
    email: string,
    firstName: string,
    lastName: string,
    // Undefined leaves memberships alone — see CreateEditUserModal.
    role: UserRoles | undefined,
    userId: string,
  ) => void;
  // common
  count: number;
  users: User[];
  organizations: Organization[];
  // search
  search: string;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // status filter
  filter: string;
  handleFilterChange: (filter: string) => void;
  // role filter
  roleFilter: UserRoles | null;
  handleRoleFilterChange: (role: UserRoles | null) => void;
  // organization filter
  organizationFilter: string | null;
  handleOrganizationFilterChange: (organizationId: string | null) => void;
  // createdAt filter (preset in days)
  createdAtDays: number | null;
  handleCreatedAtDaysChange: (days: number | null) => void;
  // clear all filters
  handleClearFilters: () => void;
  // order
  orderDirection: 'asc' | 'desc';
  orderBy: string | null;
  handleRequestSort: (property: string) => void;
  // pagination
  page: number;
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => void;
}

const AdminList: React.FC<UserListProps> = ({
  count,
  users,
  organizations,
  search,
  handleSearchChange,
  filter,
  handleFilterChange,
  roleFilter,
  handleRoleFilterChange,
  organizationFilter,
  handleOrganizationFilterChange,
  createdAtDays,
  handleCreatedAtDaysChange,
  handleClearFilters,
  orderBy,
  orderDirection,
  handleRequestSort,
  page,
  handleChangePage,
  rowsPerPage,
  handleDisableUser,
  handleEnableUser,
  handleEditUser,
}) => {
  const [showConfirmDisableModal, setShowConfirmDisableModal] =
    useState<boolean>(false);
  const [showConfirmEnableModal, setShowConfirmEnableModal] =
    useState<boolean>(false);
  //TODO: Unify these
  const [userToDisable, setuserToDisable] = useState<User | undefined>(
    undefined,
  );
  // Held by id, not by value: editing a membership refetches the list, and a
  // frozen copy would keep showing the role that was just changed away from.
  const [userToEditId, setUserToEditId] = useState<string | undefined>(
    undefined,
  );
  const userToEdit = users.find((user) => user.userId === userToEditId);
  const [userToEnable, setuserToEnable] = useState<User | undefined>(undefined);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [savingOrganizationId, setSavingOrganizationId] = useState<
    string | null
  >(null);
  const updateOrganizationRole = useUpdateUserOrganizationRole();

  const changeMembershipRole = (organizationId: string, role: string) => {
    if (!userToEdit?.userId) return;
    setSavingOrganizationId(organizationId);
    updateOrganizationRole.mutate(
      { userId: userToEdit.userId, role, organizationId },
      { onSettled: () => setSavingOrganizationId(null) },
    );
  };

  const toggleConfirmDeleteModal = (user?: User) => {
    setuserToDisable(user);
    setShowConfirmDisableModal(true);
  };

  const toggleEnableConfirmModal = (user?: User) => {
    setuserToEnable(user);
    setShowConfirmEnableModal(true);
  };

  const toggleEditModal = (user: User) => {
    setUserToEditId(user.userId);
    setShowEditModal(true);
  };

  const disableUser = () => {
    if (showConfirmDisableModal) {
      handleDisableUser(userToDisable?.userId || '');
    }
    setShowConfirmDisableModal(false);
    setuserToDisable(undefined);
  };

  const enableUser = () => {
    if (showConfirmEnableModal) {
      handleEnableUser(userToEnable?.userId || '');
    }
    setShowConfirmEnableModal(false);
    setuserToEnable(undefined);
  };

  const modalClose = () => {
    setShowConfirmDisableModal(false);
    setShowEditModal(false);
  };

  const sortedTableHeaderCell = ({
    label,
    property,
    align,
  }: {
    label: string;
    property: string;
    align?: 'right' | 'left' | 'center' | 'inherit' | 'justify' | undefined;
  }) => {
    const alignClass =
      align === 'center'
        ? 'text-center'
        : align === 'right'
          ? 'text-right'
          : 'text-left';

    const sortIcon =
      orderBy === property ? (orderDirection === 'asc' ? ' ↑' : ' ↓') : '';

    return (
      <th
        className={`px-4 py-3 ${alignClass} text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600`}
        onClick={() => handleRequestSort(property)}>
        {label}
        {sortIcon}
      </th>
    );
  };

  const activeFilterItems: SelectOption[] = [
    { value: 'All', label: 'All' },
    { value: 'Active', label: 'Active' },
    { value: 'Disabled', label: 'Disabled' },
  ];

  const roleFilterItems: SelectOption[] = Object.values(UserRoles).map(
    (role) => ({ value: role, label: role.replace(/_/g, ' ') }),
  );

  const organizationFilterItems: SelectOption[] = organizations.map((org) => ({
    value: org.organizationId,
    label: org.name,
  }));

  const createdAtFilterItems: SelectOption[] = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
  ];

  const hasActiveFilters =
    filter !== 'Active' ||
    !!roleFilter ||
    !!organizationFilter ||
    !!createdAtDays ||
    !!search;


  return (
    <div className="w-full">
      <div className="flex flex-wrap items-end mb-4 gap-3">
        <div className="min-w-[250px] flex-1">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Search
          </label>
          <Input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search users..."
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div className="min-w-[150px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Status
          </label>
          <ReactSelect
            value={
              activeFilterItems.find((item) => item.value === filter) || null
            }
            onChange={(value) => {
              const selectedValue = value as SelectOption | null;
              handleFilterChange(selectedValue?.value?.toString() || 'All');
            }}
            options={activeFilterItems}
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            isClearable={false}
            isSearchable={false}
          />
        </div>
        <div className="min-w-[180px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Role
          </label>
          <ReactSelect
            value={
              roleFilterItems.find((item) => item.value === roleFilter) || null
            }
            onChange={(value) => {
              const selectedValue = value as SelectOption | null;
              handleRoleFilterChange(
                (selectedValue?.value?.toString() as UserRoles) || null,
              );
            }}
            options={roleFilterItems}
            placeholder="All roles"
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            isClearable
            isSearchable={false}
          />
        </div>
        <div className="min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Company
          </label>
          <ReactSelect
            value={
              organizationFilterItems.find(
                (item) => item.value === organizationFilter,
              ) || null
            }
            onChange={(value) => {
              const selectedValue = value as SelectOption | null;
              handleOrganizationFilterChange(
                selectedValue?.value?.toString() || null,
              );
            }}
            options={organizationFilterItems}
            placeholder="All companies"
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            isClearable
            isSearchable
          />
        </div>
        <div className="min-w-[180px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Created
          </label>
          <ReactSelect
            value={
              createdAtFilterItems.find(
                (item) => item.value === createdAtDays,
              ) || null
            }
            onChange={(value) => {
              const selectedValue = value as SelectOption | null;
              handleCreatedAtDaysChange(
                selectedValue?.value != null
                  ? Number(selectedValue.value)
                  : null,
              );
            }}
            options={createdAtFilterItems}
            placeholder="Any time"
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            isClearable
            isSearchable={false}
          />
        </div>
        {hasActiveFilters && (
          <div>
            <Button
              variant="tertiary"
              onClick={handleClearFilters}
              title="Clear all filters">
              Clear filters
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
        <div className="overflow-x-auto">
          {/* table-fixed with explicit widths: the content decides nothing, so a
              long name or a user in six organizations can no longer push the
              table past the viewport. */}
          <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-600">
            <colgroup>
              <col className="w-[26%]" />
              <col className="w-[36%]" />
              <col className="w-[13%]" />
              <col className="w-[15%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {/* Name and email share a cell: two columns of the same identity
                    were the widest thing on the row. */}
                {sortedTableHeaderCell({ label: 'User', property: 'name' })}
                {/* Role lives per organization, so the two are one column. The
                    sort still keys off the user's default organization. */}
                {sortedTableHeaderCell({
                  label: 'Organizations & Roles',
                  property: 'organization',
                })}
                {sortedTableHeaderCell({
                  label: 'Created',
                  property: 'createdAt',
                })}
                {sortedTableHeaderCell({
                  label: 'Last Login',
                  property: 'lastLogin',
                })}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
              {users.map((row: User, idx) => (
                <tr
                  key={`${idx}-${row.firstName}`}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  {/* Truncation is CSS now, not slice(): it adapts to the column
                      instead of cutting at a fixed character count, and the full
                      value stays available on hover. */}
                  <td className="px-4 py-4 align-top text-sm">
                    <div
                      title={[row.firstName, row.lastName]
                        .filter(Boolean)
                        .join(' ')}
                      className="truncate font-medium text-gray-900 dark:text-white">
                      {[row.firstName, row.lastName].filter(Boolean).join(' ')}
                    </div>
                    <div
                      title={row.email}
                      className="truncate text-gray-500 dark:text-gray-400">
                      {row.email}
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top text-sm text-gray-900 dark:text-white">
                    <UserMembershipsCell user={row} />
                  </td>
                  <td className="px-4 py-4 align-top text-sm text-gray-900 dark:text-white">
                    {row.createdAt ? formatDateOnly(row.createdAt) : '-'}
                  </td>
                  <td className="px-4 py-4 align-top text-sm text-gray-900 dark:text-white">
                    {formatDate(row.lastLogin)}
                  </td>
                  <td className="px-4 py-4 align-top text-center">
                    <div className="flex justify-center gap-1">
                      {row.status === UserStatus.ACTIVE && (
                        <>
                          <Button
                            variant="tertiary"
                            className="flex items-center justify-center"
                            onClick={() => toggleEditModal(row)}
                            title="Edit user">
                            <EditIcon24 />
                          </Button>
                          <Button
                            variant="tertiary"
                            className="flex items-center justify-center"
                            onClick={() => toggleConfirmDeleteModal(row)}
                            title="Disable user">
                            <NoAccounts />
                          </Button>
                        </>
                      )}
                      {row.status === UserStatus.DISABLED && (
                        <Button
                          variant="tertiary"
                          className="flex items-center justify-center"
                          onClick={() => toggleEnableConfirmModal(row)}
                          title="Enable user">
                          <PersonAddAlt1Icon sx={{ fontSize: 18 }} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {count === 0
                ? '0 of 0'
                : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, count)} of ${count}`}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="tertiary"
                size="md"
                onClick={(e) => handleChangePage(e, page - 1)}
                disabled={page === 0}>
                Previous
              </Button>
              <Button
                variant="tertiary"
                size="md"
                onClick={(e) => handleChangePage(e, page + 1)}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        variant="danger"
        onClose={modalClose}
        title="Disable User"
        confirmText="Disable"
        onConfirm={disableUser}
        isOpen={showConfirmDisableModal}
        description={`Are you sure you want to disable the user ${userToDisable?.firstName} ${userToDisable?.lastName} (${userToDisable?.email})?`}
      />

      <Modal
        isOpen={showConfirmEnableModal}
        onClose={modalClose}
        title="Enable User"
        confirmText="Enable"
        handleConfirm={enableUser}
        size="small">
        <EnableUserModal
          modalClose={modalClose}
          enableUser={enableUser}
          user={userToEnable}
        />
      </Modal>

      <CreateEditUserModal
        isOpen={!!showEditModal}
        onClose={modalClose}
        editing={true}
        editUser={handleEditUser}
        organizations={organizations}
        saving={false}
        initialData={{
          userId: userToEdit?.userId || '',
          email: userToEdit?.email || '',
          firstName: userToEdit?.firstName || '',
          lastName: userToEdit?.lastName || '',
          role: userToEdit?.role || UserRoles.CREW_LEAD,
          locale: userToEdit?.locale || UserLocale.EN,
          organizationId: userToEdit?.organizationId || '',
        }}
        memberships={userToEdit?.organizationRoles}
        onChangeMembershipRole={changeMembershipRole}
        savingOrganizationId={savingOrganizationId}
      />
    </div>
  );
};

export default AdminList;
